import * as Hooks from 'preact/hooks';
import * as VariantDataStore from '../../store/variantData';
import * as Selectors from '../../store/selectors';
import { useDispatch, useSelector } from '../hooks/redux';
import axios from 'axios';
import { pipe } from 'fp-ts/lib/function';
import * as Product from '../../data/product';
import * as Gift from '../../data/gift/product';
import * as TE from 'fp-ts/TaskEither';
import { attributesFromProducts } from '../../data/productVariant';
import { ProductAttributes } from '@dora/backpack';
import * as EC from '@fixtuur/error-context';

export const useLoadVariantData = (viewDifferentVariantsEnabled: boolean) => {
    const dispatch = useDispatch();
    const tenantIdentifier = useSelector(Selectors.Core.tenantIdentifier);
    const productId = useSelector(Selectors.Core.inspirationProductId);
    const productDataUrl = useSelector(Selectors.Core.productDataUrl);

    Hooks.useEffect(() => {
        if (viewDifferentVariantsEnabled) {
            pipe(
                TE.Do,
                TE.bind('productVariantId', () =>
                    TE.fromNullable(EC.construct('variable not set'))(productId),
                ),
                TE.bind('tenantId', () =>
                    TE.fromNullable(EC.construct('variable not set'))(tenantIdentifier),
                ),
                TE.bind('dataUrl', () =>
                    TE.fromNullable(EC.construct('variable not set'))(productDataUrl),
                ),
                TE.bind('productData', ({ productVariantId, dataUrl, tenantId }) =>
                    TE.tryCatch(
                        () =>
                            axios
                                .get(`${dataUrl}/api/v1/${tenantId}/products/${productVariantId}`, {
                                    responseType: 'json',
                                })
                                .then(res => res.data),
                        reason =>
                            EC.construct('Failed to fetch product', {
                                error: (reason as any)?.message,
                                dataUrl,
                            }),
                    ),
                ),
                TE.bind('decodedData', ({ productData }) =>
                    TE.fromEither(Gift.Product.decode(productData)),
                ),
                TE.bind('groupingCode', ({ decodedData }) =>
                    TE.fromNullable(EC.construct('no grouping code'))(
                        Product.fromGiftProduct(decodedData as Gift.Product).groupingCode,
                    ),
                ),
                TE.bind('productVariants', ({ groupingCode, dataUrl, tenantId }) =>
                    TE.tryCatch(
                        () =>
                            axios
                                .get(
                                    `${dataUrl}/api/v1/${tenantId}/products?groupingCode=${groupingCode}`,
                                    {
                                        responseType: 'json',
                                    },
                                )
                                .then(res => res.data),

                        reason =>
                            EC.construct('Failed to fetch by grouping code', {
                                error: (reason as any)?.message,
                                dataUrl,
                            }),
                    ),
                ),
                TE.match(
                    err => {
                        switch (EC.toString(err)) {
                            case 'variable not set':
                                return;
                            default:
                                dispatch(
                                    VariantDataStore.setProductVariantData({
                                        productVariantAttributes:
                                            {} as ProductAttributes.AttributeProperties,
                                        productVariantOptions: [],
                                    }),
                                );
                        }
                    },
                    ({ productVariants }) => {
                        const products: Array<Product.Product> = productVariants.map(
                            Product.fromGiftProduct,
                        );
                        const supportedProducts = products.filter(p => p.discontinued !== true);
                        const variantData = attributesFromProducts(supportedProducts);

                        dispatch(
                            VariantDataStore.setProductVariantData({
                                productVariantAttributes: variantData.attributes,
                                productVariantOptions: variantData.variantOptions,
                            }),
                        );
                    },
                ),
            )();
        } else {
            dispatch(
                VariantDataStore.setProductVariantData({
                    productVariantAttributes: {} as ProductAttributes.AttributeProperties,
                    productVariantOptions: [],
                }),
            );
        }
    }, [productId, productDataUrl, tenantIdentifier]);
};
